import React from 'react';
import { useLocation, useRouter } from 'wouter';
import { ChevronLeft } from 'lucide-react';
import { material3 } from '@/theme/material3';
import { cn } from '@/lib/utils';
import { hapticTap } from '@/lib/hapticFeedback';

interface AppBarProps {
  title: string;
  showBack?: boolean;
  onBackClick?: () => void;
  rightAction?: React.ReactNode;
  className?: string;
}

/**
 * Material 3 AppBar component - Android-style top navigation
 * Features:
 * - Elevation/shadow for depth
 * - Centered title
 * - Optional back button with haptic feedback
 * - Responsive to dark mode
 */
export function AppBar({
  title,
  showBack = false,
  onBackClick,
  rightAction,
  className,
}: AppBarProps) {
  const [, setLocation] = useLocation();
  const router = useRouter();

  const handleBack = async () => {
    await hapticTap();
    if (onBackClick) {
      onBackClick();
    } else {
      // Navigate back in history
      window.history.back();
    }
  };

  const palette = material3.colors.light;

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full',
        'flex items-center justify-between',
        'px-4 py-3',
        'bg-[var(--surface)] text-[var(--on-surface)]',
        'border-b border-[var(--outline-variant)]',
        'shadow-md',
        className
      )}
      style={{
        '--surface': palette.surface,
        '--on-surface': palette.onSurface,
        '--outline-variant': palette.outlineVariant,
      } as React.CSSProperties}
    >
      {/* Left: Back Button */}
      <div className="flex items-center justify-start w-12">
        {showBack && (
          <button
            onClick={handleBack}
            className={cn(
              'flex items-center justify-center',
              'w-10 h-10 rounded-lg',
              'active:bg-[var(--surface-container)]',
              'transition-colors duration-200'
            )}
            aria-label="Go back"
          >
            <ChevronLeft
              size={24}
              className="text-[var(--on-surface)]"
            />
          </button>
        )}
      </div>

      {/* Center: Title */}
      <div className="flex-1 flex justify-center items-center px-4">
        <h1
          className={cn(
            'text-xl font-semibold',
            'text-[var(--on-surface)]',
            'truncate text-center'
          )}
        >
          {title}
        </h1>
      </div>

      {/* Right: Action */}
      <div className="flex items-center justify-end w-12">
        {rightAction}
      </div>
    </header>
  );
}

export default AppBar;
